import { z } from "zod";
import { ActionSettings } from "./dto";
import {
  ActionKind,
  ActionStatus,
  AssignmentResult,
  FindingCategory,
  ParticipantRole,
  SectorStatus,
  UnitSettingKey,
  UnitType,
} from "./enums";
import { FindingIcon } from "./finding-icons";
import { AreaGeometry, BBox, Latitude, Longitude, PointGeometry } from "./geo";

// Inputs of the panel REST API. Mutations accept an `Idempotency-Key` header.

const Id = z.uuid();
const Timestamp = z.iso.datetime({ offset: true });
const PhoneNumber = z.string().trim().min(3).max(30);

export const CreateActionInput = z.object({
  organizationId: Id,
  name: z.string().trim().min(2).max(120),
  kind: ActionKind.default("real"),
  hqPhone: PhoneNumber.nullish(),
  baseLocation: PointGeometry.nullish(),
  areaBbox: BBox.nullish(),
  settings: ActionSettings.partial().optional(),
});
export type CreateActionInput = z.infer<typeof CreateActionInput>;

export const UpdateActionInput = z.object({
  name: z.string().trim().min(2).max(120).optional(),
  kind: ActionKind.optional(),
  hqPhone: PhoneNumber.nullish(),
  baseLocation: PointGeometry.nullish(),
  areaBbox: BBox.nullish(),
  settings: ActionSettings.partial().optional(),
});
export type UpdateActionInput = z.infer<typeof UpdateActionInput>;

export const SetActionStatusInput = z.object({ status: ActionStatus });
export type SetActionStatusInput = z.infer<typeof SetActionStatusInput>;

export const UnitSettingsInput = z.object({
  settings: z
    .array(
      z.object({
        unitType: UnitSettingKey,
        sweepWidthM: z.number().positive().max(500),
        maxSpeedMps: z.number().positive().max(100),
      }),
    )
    .min(1),
});
export type UnitSettingsInput = z.infer<typeof UnitSettingsInput>;

export const UpsertSubjectInput = z.object({
  displayName: z.string().trim().min(1).max(120),
  age: z.number().int().min(0).max(130).nullish(),
  description: z.string().max(4000).nullish(),
  clothing: z.string().max(2000).nullish(),
  medicalNotes: z.string().max(4000).nullish(),
  ipp: PointGeometry.nullish(),
  ippAt: Timestamp.nullish(),
  lkp: PointGeometry.nullish(),
  lkpAt: Timestamp.nullish(),
});
export type UpsertSubjectInput = z.infer<typeof UpsertSubjectInput>;

export const CreateGroupInput = z.object({
  name: z.string().trim().min(1).max(60),
  callsign: z.string().trim().max(30).nullish(),
  color: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/)
    .nullish(),
});
export type CreateGroupInput = z.infer<typeof CreateGroupInput>;

export const UpdateGroupInput = CreateGroupInput.partial().extend({
  leaderParticipantId: Id.nullish(),
});
export type UpdateGroupInput = z.infer<typeof UpdateGroupInput>;

export const CreateInviteInput = z.object({
  label: z.string().trim().max(80).nullish(),
  role: ParticipantRole.default("searcher"),
  groupId: Id.nullish(),
  /** Validity in hours from now. */
  validHours: z
    .number()
    .int()
    .min(1)
    .max(24 * 14)
    .default(24),
  maxUses: z.number().int().min(1).max(10000).nullish(),
  requiresApproval: z.boolean().default(false),
});
export type CreateInviteInput = z.infer<typeof CreateInviteInput>;

export const UpdateParticipantInput = z.object({
  groupId: Id.nullish(),
  displayName: z.string().trim().min(2).max(60).optional(),
  role: ParticipantRole.optional(),
  /** From now on; the track so far keeps its unit (#205). */
  unitType: UnitType.optional(),
  approved: z.literal(true).optional(),
  canViewVideo: z.boolean().optional(),
});
export type UpdateParticipantInput = z.infer<typeof UpdateParticipantInput>;

/** Merge a participant who re-joined after reinstalling the app into their earlier record. */
export const MergeParticipantInput = z.object({ intoParticipantId: Id });
export type MergeParticipantInput = z.infer<typeof MergeParticipantInput>;

export const SetLiveModeInput = z.object({ live: z.boolean() });
export type SetLiveModeInput = z.infer<typeof SetLiveModeInput>;

export const CreateSectorInput = z.object({
  name: z.string().trim().min(1).max(50),
  geometry: AreaGeometry,
  priority: z.number().int().min(0).max(10).default(0),
  notes: z.string().max(2000).nullish(),
});
export type CreateSectorInput = z.infer<typeof CreateSectorInput>;

export const UpdateSectorInput = z.object({
  name: z.string().trim().min(1).max(50).optional(),
  geometry: AreaGeometry.optional(),
  status: SectorStatus.optional(),
  priority: z.number().int().min(0).max(10).optional(),
  notes: z.string().max(2000).nullish(),
});
export type UpdateSectorInput = z.infer<typeof UpdateSectorInput>;

export const AssignSectorInput = z.object({ groupId: Id });
export type AssignSectorInput = z.infer<typeof AssignSectorInput>;

export const UpdateAssignmentInput = z.object({
  started: z.literal(true).optional(),
  result: AssignmentResult.optional(),
});
export type UpdateAssignmentInput = z.infer<typeof UpdateAssignmentInput>;

export const SendMessageInput = z.object({
  channel: z.string().max(80),
  body: z.string().trim().min(1).max(2000),
});
export type SendMessageInput = z.infer<typeof SendMessageInput>;

/** HQ has read the channel up to this message (`eventSeq`), issues #110, #111. */
export const ChatReadInput = z.object({
  channel: z.string().max(80),
  seq: z.number().int().nonnegative(),
});
export type ChatReadInput = z.infer<typeof ChatReadInput>;

/** The signed-in staff member's read position per channel: `{ "action": 120, "group:…": 98 }`. */
export type ChatReadsDto = Record<string, number>;

export const ListMessagesQuery = z.object({
  channel: z.string().max(80).optional(),
  beforeSeq: z.coerce.number().int().positive().optional(),
  limit: z.coerce.number().int().min(1).max(500).default(200),
});
export type ListMessagesQuery = z.infer<typeof ListMessagesQuery>;

export const CreateFindingInput = z.object({
  lat: Latitude,
  lon: Longitude,
  category: FindingCategory,
  /** With an icon the server takes the category from it (FINDING_ICON_CATEGORY). */
  icon: FindingIcon.optional(),
  description: z.string().max(2000).nullish(),
  foundAt: Timestamp.optional(),
  /**
   * A chat photo HQ turns into a finding (issue #132): the finding gets its own copy of the
   * photo. Findings entered by HQ are shown to every phone in the action (visibility.ts).
   */
  fromPhotoId: Id.optional(),
});
export type CreateFindingInput = z.infer<typeof CreateFindingInput>;

/** HQ corrects a finding (#210): a new place, kind or description. */
export const UpdateFindingInput = z
  .object({
    lat: Latitude.optional(),
    lon: Longitude.optional(),
    category: FindingCategory.optional(),
    /** With an icon the server takes the category from it (FINDING_ICON_CATEGORY). */
    icon: FindingIcon.optional(),
    description: z.string().max(2000).nullish(),
  })
  .refine((v) => (v.lat === undefined) === (v.lon === undefined), {
    message: "lat and lon together",
  })
  .refine((v) => Object.values(v).some((x) => x !== undefined), { message: "nothing to change" });
export type UpdateFindingInput = z.infer<typeof UpdateFindingInput>;

export const IssueRecallInput = z.object({
  groupId: Id.nullish(),
  reason: z.string().trim().max(500).nullish(),
});
export type IssueRecallInput = z.infer<typeof IssueRecallInput>;

export const StreamQuery = z.object({
  /** Fallback for clients that cannot send Last-Event-ID. */
  after: z.coerce.number().int().nonnegative().optional(),
});

export const TracksQuery = z.object({
  participantId: Id.optional(),
  since: Timestamp.optional(),
  /**
   * "1": coordinates carry the time as a third value, [lon, lat, epoch seconds], for the history
   * player (issue #125). The time is the M value the track is stored with.
   */
  timed: z.literal("1").optional(),
});
export type TracksQuery = z.infer<typeof TracksQuery>;

export const CreateStaffMemberInput = z.object({
  email: z.email(),
  name: z.string().trim().min(2).max(80),
  role: z.enum(["admin", "commander", "observer"]),
  /** Temporary password handed over in person; the user changes it after first login. */
  password: z.string().min(10).max(200),
});
export type CreateStaffMemberInput = z.infer<typeof CreateStaffMemberInput>;

/** A correction of a staff account by its organization's admin (#232); only the fields sent change. */
export const UpdateStaffMemberInput = CreateStaffMemberInput.pick({
  email: true,
  name: true,
  role: true,
})
  .partial()
  .refine((v) => Object.keys(v).length > 0, "nothing to change");
export type UpdateStaffMemberInput = z.infer<typeof UpdateStaffMemberInput>;

/** A new organization with its first admin, created by an instance administrator (#230). */
export const CreateOrganizationInput = z.object({
  name: z.string().trim().min(2).max(120),
  /** Short unique name, e.g. "osp.bukownica". */
  slug: z
    .string()
    .trim()
    .toLowerCase()
    .regex(/^[a-z0-9][a-z0-9.-]{1,59}$/),
  admin: CreateStaffMemberInput.omit({ role: true }),
});
export type CreateOrganizationInput = z.infer<typeof CreateOrganizationInput>;

/** A corrected name or short name, by an instance administrator (#232). */
export const UpdateOrganizationInput = CreateOrganizationInput.pick({ name: true, slug: true })
  .partial()
  .refine((v) => Object.keys(v).length > 0, "nothing to change");
export type UpdateOrganizationInput = z.infer<typeof UpdateOrganizationInput>;
